import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import { LuRotateCw, LuKeyRound, LuCrown } from 'react-icons/lu'

import notFound from '../Contacts/Table/NotFoundData'
import TableSkeletonRows from '../../components/Skeleton/TableSkeletonRows'
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal'
import PopupWithActions from '../../components/Popup/PopupWithAction'
import { showSnackBar } from '../../components/Snackbar/Snackbar'
import { useMyContextLoading } from '../../components/LoadingContextProvider/LoadingContextProvider'
import UserStatusCell from '../Contacts/Table/Contact/StatusCell'
import {
  resendAffiliateInvite,
  changeAffiliateAccess,
  sendAffiliatePasswordReset,
  grantOfficeOwnership,
} from '../../features/affiliates/affiliates'
import { selectFeatureFlag } from '../../features/featureFlags/selectors'
import {
  AFFILIATE_OWNER_SETS_ACCESS,
  AFFILIATE_OWNER_RESETS_PASSWORD,
  AFFILIATE_OWNER_GRANTS_COOWNER,
} from '../../config/featureFlagKeys'
import useAffiliateStatusChange from '../Affiliates/useAffiliateStatusChange'
import useTeamMemberAction from './useTeamMemberAction'
import AccessCell from './AccessCell'
import {
  ACCESS_OPTIONS,
  accessOf,
  accessConfirmTitle,
  accessConfirmMessage,
  accessChangedMessage,
  RESET_TITLE,
  RESET_CONFIRM_TITLE,
  RESET_SENT,
  resetConfirmMessage,
  COOWNER_TITLE,
  coOwnerConfirmTitle,
  coOwnerConfirmLines,
  coOwnerGrantedMessage,
} from './teamActions'
import {
  AFFILIATE_STATUS_MAP,
  AFFILIATE_STATUS_OPTIONS,
  PENDING_INVITE_BADGE,
  statusConfirmTitle,
  statusConfirmMessage,
} from '../Affiliates/affiliateStatus'

const headerCellSx = {
  fontSize: '13px', textTransform: 'uppercase', fontWeight: 600,
  color: 'text.secondary', backgroundColor: 'var(--color-bg-subtle)',
  borderBottom: 1, borderColor: 'divider',
  px: '10px', py: 0, verticalAlign: 'middle', whiteSpace: 'nowrap', lineHeight: 1,
}

const bodyCellSx = {
  fontSize: '0.875rem', fontWeight: 400,
  borderBottom: 1, borderColor: 'divider',
  color: 'text.primary', px: '10px', py: 0, verticalAlign: 'middle',
}

const CLOSED = { anchorEl: null, rowId: null }

const MyTeamTable = ({ data, onChanged }) => {
  const dispatch = useDispatch()
  const { triggerLoading, isBarLoading } = useMyContextLoading()
  const accessOn = useSelector(selectFeatureFlag(AFFILIATE_OWNER_SETS_ACCESS))
  const resetOn = useSelector(selectFeatureFlag(AFFILIATE_OWNER_RESETS_PASSWORD))
  const coOwnerOn = useSelector(selectFeatureFlag(AFFILIATE_OWNER_GRANTS_COOWNER))
  const [statusMenu, setStatusMenu] = useState(CLOSED)
  const [accessMenu, setAccessMenu] = useState(CLOSED)
  const statusChange = useAffiliateStatusChange({ onSuccess: onChanged })
  const memberAction = useTeamMemberAction({ onSuccess: onChanged })
  const columns = accessOn ? 6 : 5
  const showSkeleton = isBarLoading && !(data?.length > 0)
  const NotFound = notFound

  const handleResendInvite = async (userId) => {
    triggerLoading(true)
    const response = await dispatch(resendAffiliateInvite(userId))
    if (response?.payload?.data?.success) {
      showSnackBar('success', 'Invitation email has been resent')
    } else {
      showSnackBar('error', response?.payload || 'Failed to resend invitation')
    }
    triggerLoading(false)
  }

  const closeStatusMenu = () => setStatusMenu({ anchorEl: null, rowId: null })

  const openStatusMenu = (event, rowId) => {
    if (statusChange.busy) return
    event.stopPropagation()
    const anchorEl = event.currentTarget
    setStatusMenu((prev) => (prev.rowId === rowId ? { anchorEl: null, rowId: null } : { anchorEl, rowId }))
  }

  const handleStatusSelect = (row, status) => {
    closeStatusMenu()
    statusChange.requestChange({ userId: row?._id, status, currentStatus: row?.affiliateStatus || 'active' })
  }

  const closeAccessMenu = () => setAccessMenu(CLOSED)

  const openAccessMenu = (event, rowId) => {
    if (memberAction.busy) return
    event.stopPropagation()
    const anchorEl = event.currentTarget
    setAccessMenu((prev) => (prev.rowId === rowId ? CLOSED : { anchorEl, rowId }))
  }

  const handleAccessSelect = (row, accessLevel) => {
    closeAccessMenu()
    memberAction.request({
      title: accessConfirmTitle(accessLevel),
      message: accessConfirmMessage(accessLevel),
      thunk: changeAffiliateAccess({ userId: row?._id, accessLevel }),
      success: accessChangedMessage(accessLevel),
    })
  }

  const accessRow = data?.find((r) => r?._id === accessMenu.rowId)

  const handleSendReset = (row) => memberAction.request({
    title: RESET_CONFIRM_TITLE,
    message: resetConfirmMessage(row),
    thunk: sendAffiliatePasswordReset(row?._id),
    success: RESET_SENT,
  })

  const handleMakeCoOwner = (row) => memberAction.request({
    title: coOwnerConfirmTitle(row),
    message: (
      <div className="flex flex-col gap-2">
        {coOwnerConfirmLines(row).map((line) => <p key={line}>{line}</p>)}
      </div>
    ),
    thunk: grantOfficeOwnership(row?._id),
    success: coOwnerGrantedMessage(row),
  })

  const statusBadge = (row) => (
    <UserStatusCell
      statusObj={AFFILIATE_STATUS_MAP[row?.affiliateStatus] || AFFILIATE_STATUS_MAP.active}
      arrow={Boolean(row?.canChangeStatus)}
      isOpen={statusMenu.rowId === row?._id}
    />
  )

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={headerCellSx}>Name</TableCell>
            <TableCell sx={headerCellSx}>Email</TableCell>
            <TableCell sx={headerCellSx}>Office</TableCell>
            <TableCell sx={headerCellSx}>Status</TableCell>
            {accessOn && <TableCell sx={headerCellSx}>Access</TableCell>}
            <TableCell sx={headerCellSx}>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {showSkeleton ? (
            <TableSkeletonRows rows={8} colSpan={columns} />
          ) : data?.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns} sx={{ p: 0, border: 'none' }}>
                <NotFound />
              </TableCell>
            </TableRow>
          ) : (
            data?.map((row) => (
              <TableRow key={row._id} sx={{ height: 75 }}>
                <TableCell sx={bodyCellSx}>{row?.firstName} {row?.lastName}</TableCell>
                <TableCell sx={bodyCellSx}>{row?.email}</TableCell>
                <TableCell sx={bodyCellSx}>
                  {row?.contactGroups?.map((group) => <div key={group._id}>{group.name}</div>)}
                  {row?.otherOffices && (
                    <div className="text-xs text-text-secondary">Also works in another office</div>
                  )}
                </TableCell>
                <TableCell sx={bodyCellSx}>
                  <div className="flex items-center gap-2">
                    {row?.canChangeStatus ? (
                      <button
                        type="button"
                        aria-label={`Change status of ${row?.firstName} ${row?.lastName}`}
                        className="cursor-pointer"
                        onClick={(event) => openStatusMenu(event, row?._id)}
                      >
                        {statusBadge(row)}
                      </button>
                    ) : statusBadge(row)}
                    {!row?.isPassword && <UserStatusCell statusObj={PENDING_INVITE_BADGE} />}
                  </div>
                </TableCell>
                {accessOn && (
                  <TableCell sx={bodyCellSx}>
                    <AccessCell
                      row={row}
                      isOpen={accessMenu.rowId === row?._id}
                      onOpen={(event) => openAccessMenu(event, row?._id)}
                    />
                  </TableCell>
                )}
                <TableCell sx={bodyCellSx}>
                  <div className="flex items-center justify-end gap-3 w-full pr-1">
                    {!row?.isPassword && (
                      <button
                        type="button"
                        title="Resend Invite"
                        aria-label="Resend Invite"
                        className="cursor-pointer"
                        onClick={() => handleResendInvite(row?._id)}
                      >
                        <LuRotateCw size={18} className="text-text-secondary" />
                      </button>
                    )}
                    {resetOn && row?.canResetPassword && (
                      <button
                        type="button"
                        title={RESET_TITLE}
                        aria-label={RESET_TITLE}
                        className="cursor-pointer"
                        onClick={() => handleSendReset(row)}
                      >
                        <LuKeyRound size={18} className="text-text-secondary" />
                      </button>
                    )}
                    {coOwnerOn && row?.canMakeCoOwner && (
                      <button
                        type="button"
                        title={COOWNER_TITLE}
                        aria-label={COOWNER_TITLE}
                        className="cursor-pointer"
                        onClick={() => handleMakeCoOwner(row)}
                      >
                        <LuCrown size={18} className="text-text-secondary" />
                      </button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <PopupWithActions
        anchor={statusMenu.anchorEl}
        open={Boolean(statusMenu.anchorEl)}
        handleClose={closeStatusMenu}
      >
        {AFFILIATE_STATUS_OPTIONS.map((option) => {
          const row = data?.find((r) => r?._id === statusMenu.rowId)
          const isCurrent = (row?.affiliateStatus || 'active') === option.value
          return (
            <div
              key={option.value}
              onClick={() => !isCurrent && !statusChange.busy && handleStatusSelect(row, option.value)}
              className={`p-[14px] rounded-md capitalize ${isCurrent || statusChange.busy
                ? 'text-text-disabled cursor-default'
                : 'cursor-pointer text-text-primary hover:bg-surface'}`}
            >
              Set {option.value}
            </div>
          )
        })}
      </PopupWithActions>
      {accessOn && (
        <PopupWithActions
          anchor={accessMenu.anchorEl}
          open={Boolean(accessMenu.anchorEl)}
          handleClose={closeAccessMenu}
        >
          {ACCESS_OPTIONS.map((option) => {
            const isCurrent = accessOf(accessRow) === option.value
            return (
              <div
                key={option.value}
                onClick={() => !isCurrent && !memberAction.busy && handleAccessSelect(accessRow, option.value)}
                className={`p-[14px] rounded-md ${isCurrent || memberAction.busy
                  ? 'text-text-disabled cursor-default'
                  : 'cursor-pointer text-text-primary hover:bg-surface'}`}
              >
                Set {option.label}
              </div>
            )
          })}
        </PopupWithActions>
      )}
      {memberAction.pending && (
        <ConfirmModal
          title={memberAction.title}
          modalButtonTitle="Confirm"
          open={memberAction.pending}
          setOpen={(val) => !val && memberAction.cancel()}
          functionButton={memberAction.confirm}
        >
          {memberAction.message}
        </ConfirmModal>
      )}
      {statusChange.pending && (
        <ConfirmModal
          title={statusConfirmTitle(statusChange.targetStatus)}
          modalButtonTitle="Confirm"
          open={statusChange.pending}
          setOpen={(val) => !val && statusChange.cancel()}
          functionButton={statusChange.confirm}
        >
          {statusConfirmMessage({ status: statusChange.targetStatus, isOwner: statusChange.isOwner })}
        </ConfirmModal>
      )}
    </TableContainer>
  )
}

export default MyTeamTable
